import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const STORAGE_KEY = "dstl_lang";

const savedLanguage =
  (typeof window !== "undefined" && window.localStorage
    ? window.localStorage.getItem(STORAGE_KEY)
    : null) || "en";

const resources = {
  en: {
    translation: {
      // Language names
      "lang.english": "English",
      "lang.kinyarwanda": "Kinyarwanda",

      // Generic
      "common.loadingSession": "Loading session...",
      "common.logout": "Logout",

      // Support Page
      "support.analystHelpText":
        "Hamagara umusesenguzi wawe kugira ngo agufashe gusobanukirwa raporo, kumenya aho ibintu bigana, cyangwa gusaba isesengura rishya.",

      // Backend-related keywords
      "backend.columns": "Inkingi",
      "backend.rows": "Imirongo",
      "backend.actions": "Ibikorwa",
      "backend.submit": "Ohereza",
      "backend.newEntry": "Injiza Icyinjijwe Gishya",
      "backend.viewData": "Reba Amakuru",
      "backend.addRow": "Ongeraho Umurongo",
      "backend.uploadCSV": "Ohereza CSV",
      "backend.downloadTemplate": "Kuramo Inyandiko Y'icyitegererezo",

      // Auth / Login
      "auth.signIn": "Sign In",
      "auth.accessDashboard": "Access your DSTL dashboard",
      "auth.useOrgCredentials":
        "Use your organization-issued credentials. If you just received an invitation email, activate your account before signing in.",
      "auth.email": "Email",
      "auth.password": "Password",
      "auth.emailPlaceholder": "you@example.com",
      "auth.passwordPlaceholder": "Enter your password",
      "auth.noAccount": "Don't have an account?",
      "auth.loginFailed": "Login failed",

      // Header
      "header.profile.ariaLabel": "Open profile",

      // Navigation
      "nav.dashboard": "Dashboard",
      "nav.dataEntry": "Data Entry",
      "nav.inventory": "Inventory",
      "nav.reports": "Reports",
      "nav.chat": "Chat",

      // Platform & multi-tenant concepts
      "platform.multiTenantSystem": "Multi-tenant data management system",
      "platform.dstlPlatform": "DSTL Platform",
      "platform.organization": "Organization",
      "platform.organizationAdmin": "Organization admin",
      "platform.clientPortal": "Client portal",
      "platform.analystPortal": "Analyst portal",
      "platform.adminPortal": "Admin portal",
      "platform.onboarding": "Onboarding",
      "platform.clientOnboarding": "Client onboarding",
      "platform.assignedAnalyst": "Assigned analyst",

      // Analytics, dashboards & reports
      "analytics.dashboard": "Analytics dashboard",
      "analytics.inventoryAnalytics": "Inventory analytics",
      "analytics.reportsInsights": "Reports & Insights",
      "analytics.report": "Report",
      "analytics.analysisReport": "Analysis report",
      "analytics.pendingReports": "Pending reports",
      "analytics.completedReports": "Completed reports",
      "analytics.unseenReports": "Unseen reports",
      "analytics.seenReports": "Seen reports",
      "analytics.reportStatus": "Report status",
      "analytics.dataset": "Dataset",
      "analytics.datasetSnapshot": "Dataset snapshot",
      "analytics.trendAnalysis": "Trend analysis",
      "analytics.activityTrends": "Activity trends",
      "analytics.healthStats": "Health stats",
      "analytics.healthOfDatasets": "Health of datasets",
      "analytics.keyMetrics": "Key metrics",
      "analytics.kpis": "Key Performance Indicators (KPIs)",

      // Inventory & stock terminology
      "inventory.inventory": "Inventory",
      "inventory.items": "Inventory items",
      "inventory.overview": "Inventory overview",
      "inventory.stockMovement": "Stock movement",
      "inventory.stockStatus": "Stock status",
      "inventory.urgent": "Urgent",
      "inventory.gip": "GIP (Goods In Progress)",
      "inventory.finished": "Finished",
      "inventory.deadStock": "Dead stock",
      "inventory.lowStock": "Low stock",
      "inventory.bulkUpload": "Bulk upload",
      "inventory.exportCsv": "Export CSV",
      "inventory.filterByStatus": "Filter by status",
      "inventory.filterBySupplier": "Filter by supplier",
      "inventory.expiryFrom": "Expiry date from",
      "inventory.expiryTo": "Expiry date to",
      "inventory.depleteAssignment": "Deplete assignment",
      "inventory.template": "Template",

      // Data & records
      "data.table": "Data table",
      "data.rows": "Data rows",
      "data.viewData": "View data",
      "data.viewDataset": "View dataset",
      "data.linkedDataInvalid": "Linked data is invalid or has been removed",
      "data.circularJson": "Converting circular structure to JSON",

      // Chat & notifications
      "chat.conversation": "Conversation",
      "chat.unreadMessages": "Unread messages",
      "chat.readMessages": "Read messages",
      "chat.markAsRead": "Mark as read",
      "chat.realTimeNotifications": "Real-time notifications",
      "chat.newReportNotification": "New report notification",

      // Auth / user management extras
      "auth.setupPassword": "Setup password",
      "auth.resetPassword": "Reset password",
      "auth.resetLink": "Password reset link",
      "auth.accountActivation": "Account activation",
      "auth.accountAccessIssues": "Account access issues",
      "profile.profile": "Profile",
      "profile.clientProfile": "Client profile",
      "profile.analystProfile": "Analyst profile",
      "profile.nationalId": "National ID",
      "profile.bankAccount": "Bank account (Equity Bank)",
      "profile.termsPrivacy": "Terms & Privacy Policy",
      "profile.privacyDataUse": "Privacy & Data Use",

      // Support & help
      "support.support": "Support",
      "support.platformSupport": "DSTL platform support",
      "support.contactSupport": "Contact support",
      "support.questionsAboutData": "If you have questions about your data…",
      "support.accountAccessIssues":
        "For account access issues, contact the central support team.",
      "support.contactAnytime": "You can contact support at any time.",

      // Status & actions (UI labels)
      "actions.deleteUser": "Delete user",
      "actions.deleteUserConfirm":
        "Are you sure you want to delete this user?",
      "actions.sendResetLink": "Send reset link",
      "actions.resetPassword": "Reset password",
      "actions.viewReport": "View report",
      "actions.viewData": "View data",
      "actions.download": "Download",
      "actions.upload": "Upload",
      "actions.saveChanges": "Save changes",
      "actions.cancel": "Cancel",
      "actions.confirm": "Confirm",
      "actions.cannotUndo": "This action cannot be undone.",

      // Home / landing
      "home.title": "Welcome to DSTL Platform",
      "home.subtitle":
        "Multi-tenant data management and inventory analytics system",
      "home.featureDataTitle": "Data Management",
      "home.featureDataText":
        "Dynamic datasets for sales, stock, and custom data",
      "home.featureInventoryTitle": "Inventory Analytics",
      "home.featureInventoryText":
        "Real-time inventory tracking and status monitoring",
      "home.featureReportsTitle": "Reports & Insights",
      "home.featureReportsText":
        "Comprehensive reporting and data analysis",

      // Client dashboard / common dashboard
      "dashboard.loading": "Loading dashboard...",
      "dashboard.noOrganization":
        "No organization assigned. Please contact your administrator.",
      "dashboard.welcomeBack": "Welcome back",
      "dashboard.clientFallback": "Client",
      "dashboard.organizationPrefix": "Organization: ",
      "dashboard.keepTrack":
        "Keep track of your datasets, reports, and inventory below.",
      "dashboard.totalDatasets": "Total Datasets",
      "dashboard.thisMonth": "this month",
      "dashboard.reportsGenerated": "Reports Generated",
      "dashboard.pendingReview": "pending review",
      "dashboard.inventoryStatusDistribution": "Inventory Status Distribution",
      "dashboard.recentActivity": "Recent Activity",
      "dashboard.noRecentActivity": "No recent activity",

      // Admin dashboard
      "admin.dashboardTitle": "Admin Dashboard",
      "admin.fallbackName": "Admin",
      "admin.overviewIntro": "Here's your platform overview at a glance.",
      "admin.totalClients": "Total Clients",
      "admin.totalAnalysts": "Total Analysts",
      "admin.totalReports": "Total Reports",
      "admin.activeOrganizations": "Active Organizations",
      "admin.overviewCharts": "Overview Charts",
      "admin.overviewChartsSubtitle":
        "Visual breakdown of users and organizational activity.",
      "admin.usersByRole": "Users by Role",
      "admin.organizationsVsReports": "Organizations vs Reports",
      "admin.welcomeText": "Welcome to the DSTL administration portal.",
      "admin.registerUser": "Register User",
      "admin.users": "Users",
      "admin.organizations": "Organizations",
      "admin.assignAnalyst": "Assign Analyst",
      "admin.viewReports": "View Reports",

      // Admin / users
      "admin.loadStatsFailed": "Failed to load dashboard stats",
      "admin.loadUsersFailed": "Failed to load users",
      "admin.loadOrganizationsFailed": "Failed to load organizations",
      "admin.loadUserDetailsFailed": "Failed to load user details",
      "admin.userUpdated": "User updated successfully",
      "admin.updateUserFailed": "Failed to update user",
      "admin.userDeleted": "User deleted successfully",
      "admin.deleteUserFailed": "Failed to delete user",
      "admin.resetLinkSent": "Password reset link sent to client",
      "admin.sendResetFailed": "Failed to send reset link",
      "admin.enterNewPassword": "Please enter a new password.",
      "admin.analystPasswordUpdated": "Analyst password updated",
      "admin.updateAnalystPasswordFailed":
        "Failed to update analyst password",
      "admin.searchUsersPlaceholder": "Search by name or email...",
      "admin.noUsers": "No users found.",
      "admin.editUser": "Edit User",
      "admin.newPassword": "New Password",
      "admin.savePassword": "Save Password",

      // Analyst portal
      "analyst.dashboardTitle": "Analyst Dashboard",
      "analyst.assignedClients": "Assigned Clients",
      "analyst.analysisModels": "Analysis & Models",
      "analyst.designReport": "Design Report",
      "analyst.createAnalysis": "Create Analysis",
      "analyst.analysisResults": "Analysis Results",

      // Common labels
      "common.search": "Search",
      "common.name": "Name",
      "common.role": "Role",
      "common.status": "Status",
      "common.actions": "Actions",
      "common.active": "Active",
      "common.pending": "Pending",
      "common.saving": "Saving...",
      "common.refresh": "Refresh",
      "common.continue": "Continue",
      "common.back": "Back",
      "common.days": "days",

      // Inventory extra
      "inventory.noOrganization": "No organization selected",
      "inventory.loadFailed": "Failed to load inventory",
      "inventory.deleteItemTitle": "Delete inventory item",
      "inventory.deleteItemMessage":
        "Are you sure you want to delete this item?",
      "inventory.itemDeleted": "Item deleted successfully",
      "inventory.deleteFailed": "Failed to delete item",
      "inventory.itemUpdated": "Item updated successfully",
      "inventory.updateFailed": "Failed to update item",
      "inventory.itemAdded": "Item added successfully",
      "inventory.addFailed": "Failed to add item",
      "inventory.templateDownloaded": "Template downloaded",
      "inventory.downloadTemplateFailed": "Failed to download template",
      "inventory.exportSuccess":
        "Exported inventory CSV for current filters",
      "inventory.exportFailed": "Failed to export inventory",
      "inventory.csvTooShort":
        "CSV must have a header row and at least one data row",
      "inventory.noRowsFound": "No valid data rows found",
      "inventory.bulkSuccess": "Items uploaded successfully",
      "inventory.bulkValidationFailed":
        "Bulk upload validation failed. See detailed errors.",
      "inventory.uploadFailed": "Upload failed",
      "inventory.filtersTitle": "Filters & Search",
      "inventory.searchLabel": "Search Products",
      "inventory.searchPlaceholder":
        "Search by name, batch, supplier...",
      "inventory.searchHelp":
        "Search by product name, batch number, supplier, or storage location",
      "inventory.statusFilter": "Status Filter",
      "inventory.allStatus": "All Status",
      "inventory.supplierFilter": "Supplier Filter",
      "inventory.allSuppliers": "All Suppliers",
      "inventory.supplierHelp": "Filter products by their supplier",
      "inventory.expiryHelp": "Filter by expiration date range",
      "inventory.expiryToHelp": "End date for expiration filter",
      "inventory.addItem": "Add Item",
      "inventory.downloadTemplate": "Download Template",
      "inventory.uploadingCsv": "Uploading...",
      "inventory.uploadCsv": "Upload CSV",
      "inventory.totalValue": "Total Value",
      "inventory.productsTitle": "Products",
      "inventory.productCol": "Product",
      "inventory.supplierCol": "Supplier",
      "inventory.batchCol": "Batch",
      "inventory.dateAddedCol": "Date Added",
      "inventory.expirationCol": "Expiration",
      "inventory.boughtCol": "Bought",
      "inventory.soldCol": "Sold",
      "inventory.remainingCol": "Remaining",
      "inventory.lowStockThresholdCol": "Low Stock Threshold",
      "inventory.expiryThresholdCol": "Expiry Threshold",
      "inventory.unitPriceCol": "Unit Price",
      "inventory.totalValueCol": "Total Value",
      "inventory.emptyTitle": "No inventory items",
      "inventory.emptyMessage":
        "Add items manually or upload a CSV file to get started.",

      // Reports (client)
      "reports.noOrganization": "No organization selected",
      "reports.loadFailed": "Failed to load reports",
      "reports.backToList": "Back to Reports",
      "reports.status": "Status",
      "reports.description": "Description",
      "reports.content": "Content",
      "reports.generatedAt": "Report generated: ",
      "reports.loading": "Loading reports...",
      "reports.completed": "completed",
      "reports.emptyTitle": "No reports available",
      "reports.emptyMessage":
        "Reports generated by your analyst will appear here.",

      // Client setup profile / activation
      "setupProfile.nationalIdError":
        "Please enter a valid National ID (at least 10 digits)",
      "setupProfile.phoneRequired": "Phone number is required",
      "setupProfile.sectorRequired": "Please select your sector",
      "setupProfile.termsRequired":
        "You must accept the Terms & Privacy Policy",
      "setupProfile.passwordTooShort":
        "Password must be at least 8 characters",
      "setupProfile.passwordsMismatch": "Passwords do not match",
      "setupProfile.activationSuccess":
        "Account activated successfully! You can now login.",
      "setupProfile.activationFailed": "Activation failed",
      "setupProfile.titleProfile": "Complete Your Profile",
      "setupProfile.titlePassword": "Set Your Password",
      "setupProfile.step1":
        "Step 1 of 2 — Fill in your profile information",
      "setupProfile.step2":
        "Step 2 of 2 — Create a secure password",
      "setupProfile.phoneLabel": "Phone Number *",
      "setupProfile.region": "Region",
      "setupProfile.sector": "Sector *",
      "setupProfile.selectSector": "Select sector...",
      "setupProfile.privacyText1":
        "We securely store your identification, contact, and business details only to provide analytics, reports, and support inside this platform. We do not sell or share your data with third parties without your permission.",
      "setupProfile.privacyText2":
        "If you need to review or update your information, you can contact support at any time.",
      "setupProfile.agreeTerms":
        "I agree to the Terms & Privacy Policy.",
      "setupProfile.passwordPlaceholder":
        "Enter password (min 8 characters)",
      "setupProfile.confirmPassword": "Confirm Password",
      "setupProfile.confirmPasswordPlaceholder": "Confirm password",
      "setupProfile.activating": "Activating...",
      "setupProfile.activateAccount": "Activate Account",
      "setupProfile.alreadyHaveAccount": "Already have an account?",

      // Data entry (client)
      "dataEntry.steps.selectDataset": "Select or Create Dataset",
      "dataEntry.steps.selectDatasetSub":
        "Choose which dataset you want to work with.",
      "dataEntry.steps.defineColumns": "Define Columns",
      "dataEntry.steps.defineColumnsSub":
        'Use "Add Custom" to create or adjust structure.',
      "dataEntry.steps.addData": "Add Data",
      "dataEntry.steps.addDataSub":
        "Enter rows manually or upload a prepared CSV file.",
      "dataEntry.steps.reviewSubmit": "Review & Submit",
      "dataEntry.steps.reviewSubmitSub":
        "Confirm values, edit if needed, then submit to save.",
      "dataEntry.newEntry": "New Entry",
      "dataEntry.viewData": "View Data",
      "dataEntry.export": "Export",
      "dataEntry.exporting": "Exporting...",
      "dataEntry.exportCsv": "Export CSV",
      "dataEntry.addRowsManually": "Add Rows Manually",
      "dataEntry.addRowsManuallySub":
        "Best for a few records or quick corrections.",
      "dataEntry.addRow": "Add Row",
      "dataEntry.uploadFromCsv": "Upload from CSV",
      "dataEntry.uploadFromCsvSub":
        "Use the template to prepare many rows at once.",
      "dataEntry.downloadTemplate": "Download Template",
      "dataEntry.uploadCsv": "Upload CSV",
      "dataEntry.uploading": "Uploading...",
      "dataEntry.activeDataset": "Active dataset",
      "dataEntry.columnsDefined": "columns defined",
      "dataEntry.submitData": "Submit Data",
      "dataEntry.submitting": "Submitting...",
      "dataEntry.noRows":
        'No data rows. Click "+ Add Row" to start adding data.',
      "dataEntry.noOrganization":
        "No organization assigned. Please contact your administrator.",
      "dataEntry.loadingDatasets": "Loading datasets...",
      "dataEntry.noDatasetsTitle": "No datasets found.",
      "dataEntry.noDatasetsMessage":
        "Create your first dataset to start entering data.",
      "dataEntry.createDataset": "Create Dataset",
      "dataEntry.selectDatasetPrompt":
        "Select a dataset from the tabs above to start entering data.",
      "dataEntry.createDatasetTitle": "Create New Dataset",
      "dataEntry.datasetName": "Dataset Name*",
      "dataEntry.datasetLabelOptional": "Display Label (optional)",
      "dataEntry.columnDefinitions": "Column Definitions*",
      "dataEntry.addColumn": "Add Column",
      "dataEntry.noColumnsPrompt":
        'No columns defined. Click "+ Add Column" to start.',
      "dataEntry.structureCannotChange":
        "Column structure cannot be changed after dataset creation. Make sure to define all columns now.",
      "dataEntry.cancel": "Cancel",
      "dataEntry.createDatasetButton": "Create Dataset",
      "dataEntry.addColumnTitle": "Add New Column",
      "dataEntry.columnKey": "Column Key",
      "dataEntry.columnLabel": "Column Label",
      "dataEntry.columnType": "Column Type",
      "dataEntry.existingRowsInfo":
        "Existing rows will have empty values for this new column.",
      "dataEntry.addColumnButton": "Add Column",
      "dataEntry.loadingData": "Loading data...",
      "dataEntry.datasetSummary":
        "Dataset: {{name}} | {{rows}} row(s) | {{columns}} columns",
      "dataEntry.noExistingRows":
        'No data rows found. Switch to "New Entry" mode to add data.',
      "dataEntry.previous": "« Previous",
      "dataEntry.next": "Next »",
      "dataEntry.paginationSummary":
        "Showing {{shown}} of {{total}} rows (Page {{current}} of {{pages}})",
      "dataEntry.actions": "Actions",
      "dataEntry.deleteRow": "Delete row",
      "dataEntry.editRow": "Edit row",
      "dataEntry.saveRow": "Save row",
      "dataEntry.cancelEdit": "Cancel edit",
      "dataEntry.addCustom": "Add Custom",
      "dataEntry.column1Default": "Column 1",
      "dataEntry.columnAdded": "Column added successfully!",
      "dataEntry.columnLabelRequired": "Column label is required",
      "dataEntry.columnKeyRequired": "Column key is required (letters and underscores only)",
      "dataEntry.columnKeyDuplicate": "A column with this key already exists",
      "dataEntry.addColumnFailed": "Failed to add column",
      "dataEntry.datasetCreated": "Dataset created!",
      "dataEntry.datasetCreateFailed": "Failed to create dataset",
      "dataEntry.submitSuccess": "Data submitted successfully!",
      "dataEntry.submitFailed": "Failed to submit data",
      "dataEntry.deleteRowConfirmTitle": "Delete row?",
      "dataEntry.deleteRowConfirmMsg": "Are you sure you want to delete this row? This cannot be undone.",
      "dataEntry.deleteRowSuccess": "Row deleted",
      "dataEntry.deleteRowFailed": "Failed to delete row",
      "dataEntry.exportFailed": "Export failed",
      "dataEntry.bulkUploadSuccess": "Rows uploaded successfully!",
      "dataEntry.bulkUploadFailed": "Bulk upload failed",
      "dataEntry.templateDownloaded": "Template downloaded",
      "dataEntry.templateFailed": "Failed to download template",
      "dataEntry.noActiveDataset": "No active dataset selected.",

      // Column types (used as badges under column headers)
      "columnType.text": "Text",
      "columnType.number": "Number",
      "columnType.date": "Date",
      "columnType.boolean": "Yes/No",
      "columnType.email": "Email",
      "columnType.phone": "Phone",
      "columnType.select": "Select",

      // Analyst new pages
      "analyst.predictions": "Predictions",
      "analyst.organisationIntelligence": "Organisation Intelligence",
    },
  },
  rw: {
    translation: {
      // NOTE: Initial Kinyarwanda values are placeholders.
      // We keep English text so that everything is still understandable
      // until proper translations are provided.

      // Language names
      "lang.english": "Icyongereza",
      "lang.kinyarwanda": "Ikinyarwanda",

      // Generic
      "common.loadingSession": "Guhita isesengura...",
      "common.logout": "Gusohoka",

      // Support Page
      "support.analystHelpText":
        "Hamagara umusesenguzi wawe kugira ngo agufashe gusobanukirwa raporo, kumenya aho ibintu bigana, cyangwa gusaba isesengura rishya.",

      // Backend-related keywords
      "backend.columns": "Inkingi",
      "backend.rows": "Imirongo",
      "backend.actions": "Ibikorwa",
      "backend.submit": "Ohereza",
      "backend.newEntry": "Injiza Icyinjijwe Gishya",
      "backend.viewData": "Reba Amakuru",
      "backend.addRow": "Ongeraho Umurongo",
      "backend.uploadCSV": "Ohereza CSV",
      "backend.downloadTemplate": "Kuramo Inyandiko Y'icyitegererezo",

      // Auth / Login
      "auth.signIn": "Sign In",
      "auth.accessDashboard": "Access your DSTL dashboard",
      "auth.useOrgCredentials":
        "Use your organization-issued credentials. If you just received an invitation email, activate your account before signing in.",
      "auth.email": "Email",
      "auth.password": "Password",
      "auth.emailPlaceholder": "you@example.com",
      "auth.passwordPlaceholder": "Enter your password",
      "auth.noAccount": "Don't have an account?",
      "auth.loginFailed": "Login failed",

      // Header
      "header.profile.ariaLabel": "Open profile",

      // Navigation
      "nav.dashboard": "Ikibaho cy’isesengura",
      "nav.dataEntry": "Injiza amakuru",
      "nav.inventory": "Ibikoresho byibitswe",
      "nav.reports": "Raporo",
      "nav.chat": "Ikiganiro",

      // Platform & multi-tenant concepts
      "platform.multiTenantSystem":
        "Sisitemu yo gucunga amakuru ku bayikoresha benshi",
      "platform.dstlPlatform": "Urubuga rwa DSTL",
      "platform.organization": "Ishyirahamwe / Umuryango",
      "platform.organizationAdmin": "Ubuyobozi bw’Ishyirahamwe",
      "platform.clientPortal": "Urubuga rw’Umukiriya",
      "platform.analystPortal": "Urubuga rw’Umusesenguzi",
      "platform.adminPortal": "Urubuga rw’Ubuyobozi",
      "platform.onboarding": "Kwiyandikisha / Gutangira gukoresha",
      "platform.clientOnboarding":
        "Kwakira no gutangira gukorana n’Umukiriya",
      "platform.assignedAnalyst": "Umusesenguzi wahawe inshingano",

      // Analytics, dashboards & reports
      "analytics.dashboard": "Ikibaho cy’isesengura",
      "analytics.inventoryAnalytics": "Isesengura ry’ibyibitswe",
      "analytics.reportsInsights": "Raporo n’Ibitekerezo",
      "analytics.report": "Raporo",
      "analytics.analysisReport": "Raporo y’Isesengura",
      "analytics.pendingReports": "Raporo zitararangira",
      "analytics.completedReports": "Raporo zakozwe",
      "analytics.unseenReports": "Raporo zitarebwe",
      "analytics.seenReports": "Raporo zarebwe",
      "analytics.reportStatus":
        "Imiterere ya raporo (Itarangiye, Irimo gukorwa, Yarangiye)",
      "analytics.dataset": "Igitabo cy’amakuru",
      "analytics.datasetSnapshot": "Ifoto y’icyiciro cy’amakuru",
      "analytics.trendAnalysis": "Isesengura ry’imigendekere",
      "analytics.activityTrends": "Imigendekere y’ibikorwa",
      "analytics.healthStats": "Ibyerekeye ubuzima bw’amakuru",
      "analytics.healthOfDatasets": "Ubuzima bw’ibitabo by’amakuru",
      "analytics.keyMetrics": "Ibipimo by’ingenzi",
      "analytics.kpis": "Ibipimo by’ingenzi by’umusaruro (KPI)",

      // Inventory & stock terminology
      "inventory.inventory": "Ibikoresho byibitswe / Stock",
      "inventory.items": "Ibicuruzwa byibitswe",
      "inventory.overview": "Isesengura rusange ry’ibyibitswe",
      "inventory.stockMovement": "Imigendekere y’ibyibitswe",
      "inventory.stockStatus": "Imiterere y’ibyibitswe",
      "inventory.urgent": "Byihutirwa",
      "inventory.gip": "Ibicuruzwa biri mu nzira yo kurangira (GIP)",
      "inventory.finished": "Byarangiye",
      "inventory.deadStock": "Ibicuruzwa bitagikoreshwa / Byapfuye",
      "inventory.lowStock": "Ibikoresho bike",
      "inventory.bulkUpload": "Kohereza byinshi icyarimwe",
      "inventory.exportCsv": "Ohereza muri CSV / Kuramo CSV",
      "inventory.filterByStatus": "Siga ukurikije imiterere",
      "inventory.filterBySupplier": "Siga ukurikije utanga ibicuruzwa",
      "inventory.expiryFrom": "Itariki yo kurangira kuva",
      "inventory.expiryTo": "Itariki yo kurangira kugeza",
      "inventory.depleteAssignment": "Gusoza inshingano",
      "inventory.template": "Icyitegererezo",

      // Data & records
      "data.table": "Tebulo y’amakuru",
      "data.rows": "Imirongo y’amakuru",
      "data.viewData": "Reba amakuru",
      "data.viewDataset": "Reba igitabo cy’amakuru",
      "data.linkedDataInvalid":
        "Amakuru ahujwe siyo cyangwa yakuweho",
      "data.circularJson":
        "Guhindura imiterere y’uruhererekane mu buryo bwa JSON",

      // Chat & notifications
      "chat.conversation": "Ikiganiro",
      "chat.unreadMessages": "Ubutumwa butaramenyekana",
      "chat.readMessages": "Ubutumwa bwasomwe",
      "chat.markAsRead": "Shyira ku basomwe",
      "chat.realTimeNotifications": "Amakuru y’ako kanya",
      "chat.newReportNotification": "Itangazo rya raporo nshya",

      // Auth / user management extras
      "auth.setupPassword": "Shiraho ijambo ry’ibanga",
      "auth.resetPassword": "Subiramo ijambo ry’ibanga",
      "auth.resetLink": "Uruhererekane rwo gusubiramo ijambo ry’ibanga",
      "auth.accountActivation": "Gukora konti ikora",
      "auth.accountAccessIssues": "Ibibazo byo kugera kuri konti",
      "profile.profile": "Umwirondoro",
      "profile.clientProfile": "Umwirondoro w’Umukiriya",
      "profile.analystProfile": "Umwirondoro w’Umusesenguzi",
      "profile.nationalId": "Indangamuntu y’Igihugu",
      "profile.bankAccount": "Konti ya banki (Equity Bank)",
      "profile.termsPrivacy":
        "Amategeko n’Amabwiriza y’Ubwirinzi bw’Amakuru",
      "profile.privacyDataUse":
        "Ubwirinzi n’Imikoreshereze y’Amakuru",

      // Support & help
      "support.support": "Inkunga / Ubufasha",
      "support.platformSupport": "Ubufasha ku rubuga rwa DSTL",
      "support.contactSupport": "Vugana n’ubufasha",
      "support.questionsAboutData":
        "Niba ufite ibibazo ku makuru yawe…",
      "support.accountAccessIssues":
        "Ku bibazo byo kugera kuri konti… hamagara itsinda ry’ubufasha",
      "support.contactAnytime":
        "Urashobora kuvugana n’ubufasha igihe cyose.",

      // Status & actions (UI labels)
      "actions.deleteUser": "Siba umukozi",
      "actions.deleteUserConfirm":
        "Wemeza ko ushaka gusiba uyu mukozi?",
      "actions.sendResetLink":
        "Ohereza uruhererekane rwo gusubiramo ijambo ry’ibanga",
      "actions.resetPassword": "Subiramo ijambo ry’ibanga",
      "actions.viewReport": "Reba raporo",
      "actions.viewData": "Reba amakuru",
      "actions.download": "Kuramo",
      "actions.upload": "Kohereza",
      "actions.saveChanges": "Bika impinduka",
      "actions.cancel": "Hagarika",
      "actions.confirm": "Emeza",
      "actions.cannotUndo": "Ibi ntibishobora gusubizwa inyuma.",

      // Home / landing
      "home.title": "Ikaze ku rubuga rwa DSTL",
      "home.subtitle":
        "Sisitemu yo gucunga amakuru no gusesengura ibiri mu bubiko ku bigo byinshi",
      "home.featureDataTitle": "Igenzura ry’amakuru",
      "home.featureDataText":
        "Amatsinda y’amakuru ahinduka ku rwego rw’igurisha, ububiko n’andi makuru wihariye",
      "home.featureInventoryTitle": "Isesengura ry’ibyibitswe",
      "home.featureInventoryText":
        "Kugenzura mu gihe nyacyo ibiri mu bubiko n’imitere yabyo",
      "home.featureReportsTitle": "Raporo n’isesengura",
      "home.featureReportsText":
        "Raporo zisobanutse n’isesengura ryimbitse ry’amakuru",

      // Client dashboard / common dashboard
      "dashboard.loading": "Ibirimo kugenzurwa...",
      "dashboard.noOrganization":
        "Nta shyirahamwe wahujweho. Vugana n’umuyobozi wawe.",
      "dashboard.welcomeBack": "Ikaze kongera",
      "dashboard.clientFallback": "Umukiriya",
      "dashboard.organizationPrefix": "Ishyirahamwe: ",
      "dashboard.keepTrack":
        "Kurikirana amatsinda y’amakuru yawe, raporo n’iby’ibitswe hano.",
      "dashboard.totalDatasets": "Amatsinda y’amakuru yose",
      "dashboard.thisMonth": "uku kwezi",
      "dashboard.reportsGenerated": "Raporo zakozwe",
      "dashboard.pendingReview": "zitegereje kugenzurwa",
      "dashboard.inventoryStatusDistribution":
        "Icyiciro cy’imitere y’ibyibitswe",
      "dashboard.recentActivity": "Ibikorwa biheruka",
      "dashboard.noRecentActivity": "Nta bikorwa biheruka bihari",

      // Admin dashboard
      "admin.dashboardTitle": "Ikibaho cy’ubuyobozi",
      "admin.fallbackName": "Umuyobozi",
      "admin.overviewIntro":
        "Incamake y’uko urubuga ruhagaze muri rusange.",
      "admin.totalClients": "Abakiriya bose",
      "admin.totalAnalysts": "Abasesenguzi bose",
      "admin.totalReports": "Raporo zose",
      "admin.activeOrganizations": "Amashyirahamwe akora",
      "admin.overviewCharts": "Imbonerahamwe rusange",
      "admin.overviewChartsSubtitle":
        "Igaragaza uko abakoresha n’amashyirahamwe bakora.",
      "admin.usersByRole": "Abakoresha hakurikijwe inshingano",
      "admin.organizationsVsReports": "Amashyirahamwe ugereranyije na raporo",
      "admin.welcomeText":
        "Ikaze ku rubuga rw’ubuyobozi rwa DSTL.",
      "admin.registerUser": "Kwiyandikisha k’umukoresha",
      "admin.users": "Abakoresha",
      "admin.organizations": "Amashyirahamwe",
      "admin.assignAnalyst": "Gutanga umusesenguzi",
      "admin.viewReports": "Reba raporo",

      // Admin / users
      "admin.loadStatsFailed":
        "Ntibyakunze kubona imibare y’ikibaho cy’ubuyobozi",
      "admin.loadUsersFailed": "Ntibyakunze kubona urutonde rw’abakoresha",
      "admin.loadOrganizationsFailed":
        "Ntibyakunze kubona urutonde rw’amashyirahamwe",
      "admin.loadUserDetailsFailed":
        "Ntibyakunze kubona ibisobanuro by’umukoresha",
      "admin.userUpdated": "Umukoresha avuguruwe neza",
      "admin.updateUserFailed": "Ntibyakunze kuvugurura umukoresha",
      "admin.userDeleted": "Umukoresha yasibwe neza",
      "admin.deleteUserFailed": "Ntibyakunze gusiba umukoresha",
      "admin.resetLinkSent":
        "Uruhererekane rwo gusubiramo ijambo ry’ibanga rwoherejwe ku mukiriya",
      "admin.sendResetFailed":
        "Ntibyakunze kohereza uruhererekane rwo gusubiramo ijambo ry’ibanga",
      "admin.enterNewPassword": "Andika ijambo ry’ibanga rishya.",
      "admin.analystPasswordUpdated":
        "Ijambo ry’ibanga ry’umusesenguzi ryahinduwe",
      "admin.updateAnalystPasswordFailed":
        "Ntibyakunze guhindura ijambo ry’ibanga ry’umusesenguzi",
      "admin.searchUsersPlaceholder":
        "Shakisha hakurikijwe izina cyangwa email...",
      "admin.noUsers": "Nta bakoresha babonetse.",
      "admin.editUser": "Hindura amakuru y’umukoresha",
      "admin.newPassword": "Ijambo ry’ibanga rishya",
      "admin.savePassword": "Bika ijambo ry’ibanga",

      // Analyst portal
      "analyst.dashboardTitle": "Ikibaho cy’umusesenguzi",
      "analyst.assignedClients": "Abakiriya bahawe umusesenguzi",
      "analyst.analysisModels": "Isesengura n’inyigo",
      "analyst.designReport": "Tegura raporo",
      "analyst.createAnalysis": "Hanga isesengura",
      "analyst.analysisResults": "Ibisubizo by’isesengura",

      // Common labels
      "common.search": "Shakisha",
      "common.name": "Izina",
      "common.role": "Inshingano",
      "common.status": "Imiterere",
      "common.actions": "Ibikorwa",
      "common.active": "Akora",
      "common.pending": "Itaratangira",
      "common.saving": "Ibirimo kubikwa...",
      "common.refresh": "Ongera usubizeho",
      "common.continue": "Komeza",
      "common.back": "Subira inyuma",
      "common.days": "iminsi",

      // Inventory extra
      "inventory.noOrganization": "Nta shyirahamwe ryatoranijwe",
      "inventory.loadFailed": "Ntibyakunze kubona urutonde rw’ibyibitswe",
      "inventory.deleteItemTitle": "Siba igicuruzwa cyibitswe",
      "inventory.deleteItemMessage":
        "Wemeza ko ushaka gusiba iki gicuruzwa?",
      "inventory.itemDeleted": "Igicuruzwa cyasibwe neza",
      "inventory.deleteFailed": "Ntibyakunze gusiba igicuruzwa",
      "inventory.itemUpdated": "Igicuruzwa cyavuguruwe neza",
      "inventory.updateFailed": "Ntibyakunze kuvugurura igicuruzwa",
      "inventory.itemAdded": "Igicuruzwa cyongewe neza",
      "inventory.addFailed": "Ntibyakunze kongeraho igicuruzwa",
      "inventory.templateDownloaded": "Icyitegererezo gikuwemo neza",
      "inventory.downloadTemplateFailed":
        "Ntibyakunze gukuramo icyitegererezo",
      "inventory.exportSuccess":
        "CSV y’ibyibitswe yoherejwe hakurikijwe amasefuro",
      "inventory.exportFailed":
        "Ntibyakunze kohereza amakuru y’ibyibitswe",
      "inventory.csvTooShort":
        "CSV igomba kugira umurongo w’inyandiko n’ikirimo amakuru nibura umwe",
      "inventory.noRowsFound":
        "Nta mirongo y’amakuru ikwiriye yabonetse",
      "inventory.bulkSuccess":
        "Ibicuruzwa byinjiye neza muri sisitemu",
      "inventory.bulkValidationFailed":
        "Kugenzura CSV byananiranye. Reba amakosa arambuye.",
      "inventory.uploadFailed": "Kohereza byananiranye",
      "inventory.filtersTitle": "Ibyo ushyiraho imbere & Ishakisha",
      "inventory.searchLabel": "Shakisha ibicuruzwa",
      "inventory.searchPlaceholder":
        "Shakisha hakurikijwe izina, batch, cyangwa utanga ibicuruzwa...",
      "inventory.searchHelp":
        "Shakisha hakurikijwe izina ry’igicuruzwa, nimero ya batch, utanga ibicuruzwa cyangwa aho kibitswe",
      "inventory.statusFilter": "Sefa hakurikijwe imiterere",
      "inventory.allStatus": "Imiterere yose",
      "inventory.supplierFilter": "Sefa hakurikijwe utanga ibicuruzwa",
      "inventory.allSuppliers": "Abatanga bose",
      "inventory.supplierHelp":
        "Tandukanya ibicuruzwa hakurikijwe ababitanga",
      "inventory.expiryHelp":
        "Sefa hakurikijwe igihe bizarangirira",
      "inventory.expiryToHelp":
        "Itariki ntarengwa y’igihe cyo kurangira",
      "inventory.addItem": "Ongeraho igicuruzwa",
      "inventory.downloadTemplate": "Kuramo icyitegererezo",
      "inventory.uploadingCsv": "Ibirimo koherezwa...",
      "inventory.uploadCsv": "Ohereza CSV",
      "inventory.totalValue": "Agaciro kose",
      "inventory.productsTitle": "Ibicuruzwa",
      "inventory.productCol": "Igicuruzwa",
      "inventory.supplierCol": "Ugitanga",
      "inventory.batchCol": "Batch",
      "inventory.dateAddedCol": "Itariki byinjiriwe",
      "inventory.expirationCol": "Itariki yo kurangira",
      "inventory.boughtCol": "Byinjiye",
      "inventory.soldCol": "Byasohotse",
      "inventory.remainingCol": "Ibisigaye",
      "inventory.lowStockThresholdCol": "Urwego rwo kubura",
      "inventory.expiryThresholdCol": "Iminsi mbere yo kurangira",
      "inventory.unitPriceCol": "Igiciro kuri kimwe",
      "inventory.totalValueCol": "Agaciro kose",
      "inventory.emptyTitle": "Nta bicuruzwa biri mu bubiko",
      "inventory.emptyMessage":"Ongeraho ibicuruzwa kimwe kimwe cyangwa koresha CSV wateguye mbere.",

      // Reports (client)
      "reports.noOrganization": "Nta shyirahamwe ryatoranijwe",
      "reports.loadFailed": "Ntibyakunze kubona raporo",
      "reports.backToList": "Subira ku raporo",
      "reports.status": "Imiterere",
      "reports.description": "Ibisobanuro",
      "reports.content": "Ibigize raporo",
      "reports.generatedAt": "Raporo yakozwe: ",
      "reports.loading": "Raporo zirimo gutegurwa...",
      "reports.completed": "yuzuye",
      "reports.emptyTitle": "Nta raporo ihari",
      "reports.emptyMessage":"Raporo zakozwe n’umusesenguzi wawe zizagaragara hano.",

      // Client setup profile / activation
      "setupProfile.nationalIdError":
        "Andika indangamuntu y’u Rwanda yuzuza nibura imibare 10",
      "setupProfile.phoneRequired": "Nimero ya telefone irakenewe",
      "setupProfile.sectorRequired": "Hitamo ishami ukoreramo",
      "setupProfile.termsRequired": "Ugomba kwemera Amategeko n’amabwiriza y’ubwirinzi bw’amakuru",
      "setupProfile.passwordTooShort":
        "Ijambo ry’ibanga rigomba kugira nibura inyuguti 8",
      "setupProfile.passwordsMismatch":
        "Amagambo y’ibanga wanditse ntahuye",
      "setupProfile.activationSuccess":
        "Konti yashyizweho neza! Ushobora kwinjira.",
      "setupProfile.activationFailed":
        "Gukora konti byananiranye",
      "setupProfile.titleProfile": "Uzuza umwirondoro wawe",
      "setupProfile.titlePassword": "Shiraho ijambo ry’ibanga",
      "setupProfile.step1":
        "Intambwe ya 1 muri 2 — Uzuza amakuru akuranga",
      "setupProfile.step2":
        "Intambwe ya 2 muri 2 — Hanga ijambo ry’ibanga ryizewe",
      "setupProfile.phoneLabel": "Nimero ya telefone *",
      "setupProfile.region": "Intara / akarere ukoreramo",
      "setupProfile.sector": "Ishami ukoreramo *",
      "setupProfile.selectSector": "Hitamo ishami...",
      "setupProfile.privacyText1":
        "Dufata kandi tukabika mu mutekano amakuru yawe akuranga, ay’itumanaho n’aya business gusa kugirango tuguhereze isesengura, raporo n’inkunga kuri uru rubuga. Ntiducuruza cyangwa ngo dusangize ayo makuru abandi batabiherewe uburenganzira bwawe.",
      "setupProfile.privacyText2":
        "Niba ushaka gusubiramo cyangwa kuvugurura amakuru yawe, ushobora kuvugana n’itsinda ry’ubufasha igihe icyo ari cyo cyose.",
      "setupProfile.agreeTerms":
        "Nemera Amategeko n’Amabwiriza y’Ubwirinzi bw’Amakuru.",
      "setupProfile.passwordPlaceholder":
        "Andika ijambo ry’ibanga (byibura inyuguti 8)",
      "setupProfile.confirmPassword": "Emeza ijambo ry’ibanga",
      "setupProfile.confirmPasswordPlaceholder":
        "Andika nanone ijambo ry’ibanga",
      "setupProfile.activating": "Konti iri gushyirwaho...",
      "setupProfile.activateAccount": "Shyiraho konti",
      "setupProfile.alreadyHaveAccount":
        "Usanzwe ufite konti?",

      // Data entry (client)
      "dataEntry.steps.selectDataset": "Hitamo cyangwa hindura dataset",
      "dataEntry.steps.selectDatasetSub":
        "Hitamo dataset ushaka gukoreraho.",
      "dataEntry.steps.defineColumns": "Shyiraho inkingi",
      "dataEntry.steps.defineColumnsSub":
        "Koresha \"Add Custom\" ushyireho cyangwa uhindure imiterere.",
      "dataEntry.steps.addData": "Injiza amakuru",
      "dataEntry.steps.addDataSub":
        "Injiza imirongo intoki cyangwa ukoreshe CSV wateguye.",
      "dataEntry.steps.reviewSubmit": "Suzuma ubone kohereza",
      "dataEntry.steps.reviewSubmitSub":
        "Reba niba imibare ari yo, uhindure niba bikenewe, ubone kubika.",
      "dataEntry.newEntry": "Injiza amakuru mashya",
      "dataEntry.viewData": "Reba amakuru",
      "dataEntry.export": "Ohereza",
      "dataEntry.exporting": "Ibirimo koherezwa...",
      "dataEntry.exportCsv": "Ohereza muri CSV",
      "dataEntry.addRowsManually": "Ongeraho imirongo intoki",
      "dataEntry.addRowsManuallySub":
        "Bikwiriye imirongo mike cyangwa gukosora vuba.",
      "dataEntry.addRow": "Ongeraho umurongo",
      "dataEntry.uploadFromCsv": "Ohereza CSV",
      "dataEntry.uploadFromCsvSub":
        "Koresha icyitegererezo utegure imirongo myinshi icyarimwe.",
      "dataEntry.downloadTemplate": "Kuramo icyitegererezo",
      "dataEntry.uploadCsv": "Ohereza CSV",
      "dataEntry.uploading": "CSV iri koherezwa...",
      "dataEntry.activeDataset": "Dataset iriho ubu",
      "dataEntry.columnsDefined": "inkingi zashyizweho",
      "dataEntry.submitData": "Bika amakuru",
      "dataEntry.submitting": "Ibirimo kubikwa...",
      "dataEntry.noRows":
        'Nta mirongo y’amakuru ihari. Kanda "+ Ongeraho umurongo" kugira ngo utangire.',
      "dataEntry.noOrganization":
        "Nta shyirahamwe wahujweho. Vugana n’umuyobozi wawe.",
      "dataEntry.loadingDatasets": "Amatsinda y’amakuru ari gutegurwa...",
      "dataEntry.noDatasetsTitle": "Nta dataset yabonetse.",
      "dataEntry.noDatasetsMessage":
        "Hanga dataset ya mbere kugira ngo utangire kwinjiza amakuru.",
      "dataEntry.createDataset": "Hanga dataset",
      "dataEntry.selectDatasetPrompt":
        "Hitamo dataset iri hejuru kugira ngo utangire kwinjiza amakuru.",
      "dataEntry.createDatasetTitle": "Hanga dataset nshya",
      "dataEntry.datasetName": "Izina rya dataset *",
      "dataEntry.datasetLabelOptional": "Izina rigaragara (si ngombwa)",
      "dataEntry.columnDefinitions": "Ibisobanuro by’inkingi *",
      "dataEntry.addColumn": "Ongeraho inkingi",
      "dataEntry.noColumnsPrompt":
        'Nta nkingi zashyizweho. Kanda "+ Ongeraho inkingi" kugira ngo utangire.',
      "dataEntry.structureCannotChange":
        "Imiterere y’inkingi ntishobora guhinduka nyuma yo gukora dataset. Witondere gushyiramo inkingi zose neza.",
      "dataEntry.cancel": "Hagarika",
      "dataEntry.createDatasetButton": "Hanga dataset",
      "dataEntry.addColumnTitle": "Ongeraho inkingi nshya",
      "dataEntry.columnKey": "Izina ry’inkingi (key)",
      "dataEntry.columnLabel": "Izina riyigaragaza",
      "dataEntry.columnType": "Ubwoko bw’inkingi",
      "dataEntry.existingRowsInfo":
        "Imirongo yari isanzwe izahabwa agaciro kabusa kuri iyi nkingi nshya.",
      "dataEntry.addColumnButton": "Ongeraho inkingi",
      "dataEntry.loadingData": "Amakuru ari gutegurwa...",
      "dataEntry.datasetSummary":
        "Dataset: {{name}} | {{rows}} imirongo | {{columns}} inkingi",
      "dataEntry.noExistingRows":
        'Nta mirongo y’amakuru yabonetse. Hindura ujye kuri "Injiza amakuru mashya".',
      "dataEntry.previous": "« Subira inyuma",
      "dataEntry.next": "Komeza »",
      "dataEntry.paginationSummary":
        "Igaragaza {{shown}} muri {{total}} imirongo (Ipaji {{current}} muri {{pages}})",
      "dataEntry.actions": "Ibikorwa",
      "dataEntry.deleteRow": "Siba umurongo",
      "dataEntry.editRow": "Hindura umurongo",
      "dataEntry.saveRow": "Bika umurongo",
      "dataEntry.cancelEdit": "Hagarika guhindura",
      "dataEntry.addCustom": "Ongeraho wihariye",
      "dataEntry.column1Default": "Inkingi ya 1",
      "dataEntry.columnAdded": "Inkingi yongewe neza!",
      "dataEntry.columnLabelRequired": "Izina ry'inkingi rirakenewe",
      "dataEntry.columnKeyRequired": "Izina rya sisitemu (key) rirakenewe",
      "dataEntry.columnKeyDuplicate": "Inkingi ifite iyi key isanzwe ihari",
      "dataEntry.addColumnFailed": "Ntibyakunze kongeraho inkingi",
      "dataEntry.datasetCreated": "Dataset yashyizweho!",
      "dataEntry.datasetCreateFailed": "Ntibyakunze gushyiraho dataset",
      "dataEntry.submitSuccess": "Amakuru yabonetse neza!",
      "dataEntry.submitFailed": "Ntibyakunze kohereza amakuru",
      "dataEntry.deleteRowConfirmTitle": "Siba umurongo?",
      "dataEntry.deleteRowConfirmMsg": "Wemeza ko ushaka gusiba uyu murongo? Ibi ntibishobora gusubizwa inyuma.",
      "dataEntry.deleteRowSuccess": "Umurongo wasibwe",
      "dataEntry.deleteRowFailed": "Ntibyakunze gusiba umurongo",
      "dataEntry.exportFailed": "Ntibyakunze kohereza amakuru",
      "dataEntry.bulkUploadSuccess": "Imirongo yoherejwe neza!",
      "dataEntry.bulkUploadFailed": "Kohereza byinshi byananiranye",
      "dataEntry.templateDownloaded": "Icyitegererezo cyakuweho neza",
      "dataEntry.templateFailed": "Ntibyakunze gukuramo icyitegererezo",

      "columnType.text": "Inyandiko",
      "columnType.number": "Umubare",
      "columnType.date": "Itariki",
      "columnType.boolean": "Yego/Oya",
      "columnType.email": "Imeyili",
      "columnType.phone": "Telefone",
      "columnType.select": "Hitamo",

      "analyst.predictions": "Guteganya",
      "analyst.organisationIntelligence": "Ubwenge bw'Ishyirahamwe",
    },
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: savedLanguage,
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

export function setAppLanguage(lang) {
  i18n.changeLanguage(lang);
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.setItem(STORAGE_KEY, lang);
  }
}

export function getAppLanguage() {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage.getItem(STORAGE_KEY) || "en";
  }
  return "en";
}

export default i18n;

